import { useEffect, useState, useRef } from 'react'
import { Container, Row, Col, Card, Nav, Form, Button, Badge, Modal } from 'react-bootstrap'
import { useTranslation } from "react-i18next"
import { useParams, Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { getSessionDetail, saveAttendance, saveContent, closeSession, getAvailableTeachers, assignSubstitute, removeSubstitute, addMaterial, removeMaterial, addSessionIncident, updateIncident, deleteIncident } from '../../../actions/classActions'
import Loader from '../../../components/loaders/Loader'
import Swal from 'sweetalert2'
import { toastError } from '../../../components/swal/toast'
import AxiosControl from '../../../components/axios/AxiosControl'
import Select from 'react-select'
const Axios = AxiosControl()

export default function ClassSession() {
    const { t } = useTranslation()
    const { sessionId } = useParams()
    const dispatch = useDispatch()
    const { sessionDetail, sessionStudents, loading, availableTeachers } = useSelector(state => state.classes)
    const [activeTab, setActiveTab] = useState('attendance')
    const [attendance, setAttendance] = useState({})
    const [content, setContent] = useState('')
    const [observations, setObservations] = useState('')
    const [currentSessionId, setCurrentSessionId] = useState(null)
    const [showSubstituteModal, setShowSubstituteModal] = useState(false)
    const [showCloseModal, setShowCloseModal] = useState(false)
    const [selectedTeacher, setSelectedTeacher] = useState(null)
    const [studentObs, setStudentObs] = useState({})
    const [openObsId, setOpenObsId] = useState(null)
    const [incidentStudent, setIncidentStudent] = useState('')
    const [incidentType, setIncidentType] = useState('minor')
    const [incidentDesc, setIncidentDesc] = useState('')
    const [editingIncidentId, setEditingIncidentId] = useState(null)
    const [uploadingMaterial, setUploadingMaterial] = useState(false)
    const [saveState, setSaveState] = useState('idle')

    useEffect(() => {
        setAttendance({})
        setStudentObs({})
        setOpenObsId(null)
        setContent('')
        setObservations('')
        setSaveState('idle')
        dirtyRef.current = false
        setCurrentSessionId(sessionId)
        if (sessionId) dispatch(getSessionDetail(sessionId))
    }, [dispatch, sessionId])

    useEffect(() => {
        if (sessionDetail && String(sessionDetail.id) === String(currentSessionId)) {
            if (sessionDetail.localSession?.attendance?.length > 0) {
                const att = {}
                const obs = {}
                sessionDetail.localSession.attendance.forEach(a => {
                    att[a.student || a.studentId] = a.status
                    if (a.observation) obs[a.student || a.studentId] = a.observation
                })
                setAttendance(att)
                setStudentObs(obs)
            }
            if (sessionDetail.localSession?.contentImparted) setContent(sessionDetail.localSession.contentImparted)
            if (sessionDetail.localSession?.observations) setObservations(sessionDetail.localSession.observations)
        }
    }, [sessionDetail, currentSessionId])

    const session = sessionDetail && String(sessionDetail.id) === String(currentSessionId) ? sessionDetail : null

    const buildAttendanceList = () => sessionStudents.map(s => ({
        studentId: s.id,
        studentName: s.name,
        status: attendance[s.id] || 'present',
        observation: studentObs[s.id] || ''
    }))

    const dirtyRef = useRef(false)

    const markDirty = () => { dirtyRef.current = true }

    const markAttendance = (id, status) => {
        dirtyRef.current = true
        setAttendance(prev => ({ ...prev, [id]: status }))
    }

    useEffect(() => {
        if (!session || sessionStudents.length === 0 || !dirtyRef.current) return
        setSaveState('saving')
        const timer = setTimeout(async () => {
            await dispatch(saveAttendance(session.odooSessionId || session.id, buildAttendanceList()))
            await dispatch(saveContent(session.odooSessionId || session.id, content, observations))
            dirtyRef.current = false
            setSaveState('saved')
        }, 2500)
        return () => clearTimeout(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attendance, studentObs, content, observations, session, sessionStudents])

    const handleOpenSubstituteModal = () => {
        dispatch(getAvailableTeachers())
        setShowSubstituteModal(true)
    }

    const handleAssignSubstitute = async () => {
        if (!selectedTeacher) {
            toastError(t('teacher.classes.selectTeacher'))
            return
        }
        const result = await dispatch(assignSubstitute(
            session.odooSessionId || session.id,
            selectedTeacher.teacher.id,
            selectedTeacher.teacher.name
        ))
        if (result) {
            setShowSubstituteModal(false)
            setSelectedTeacher(null)
            dispatch(getSessionDetail(sessionId))
        }
    }

    const handleRemoveSubstitute = async () => {
        const result = await Swal.fire({
            title: t('teacher.classes.removeSubstituteConfirm'),
            text: t('teacher.classes.removeSubstituteText'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('global.yes'),
            cancelButtonText: t('global.cancel')
        })
        if (result.isConfirmed) {
            const removed = await dispatch(removeSubstitute(session.odooSessionId || session.id))
            if (removed) {
                dispatch(getSessionDetail(sessionId))
            }
        }
    }

    const localSession = session?.localSession
    const hasSubstitute = localSession?.substituteTeacherId
    const isClosed = localSession?.closed || localSession?.state === 'completed'

    const handleCloseSession = async () => {
        if (!session) return
        const ok = await dispatch(closeSession(session.odooSessionId || session.id))
        if (ok) {
            setShowCloseModal(false)
            dispatch(getSessionDetail(sessionId))
        }
    }

    const sessionKey = () => session.odooSessionId || session.id

    const handleSaveIncident = async () => {
        if (!session || !incidentDesc.trim()) return
        let ok = false
        if (editingIncidentId) {
            ok = await dispatch(updateIncident(sessionKey(), editingIncidentId, { type: incidentType, description: incidentDesc }))
        } else {
            const st = sessionStudents.find(s => String(s.id) === String(incidentStudent))
            ok = await dispatch(addSessionIncident(sessionKey(), {
                studentId: incidentStudent || null,
                studentName: st?.name || '',
                type: incidentType,
                description: incidentDesc
            }))
        }
        if (ok) {
            setIncidentStudent('')
            setIncidentType('minor')
            setIncidentDesc('')
            setEditingIncidentId(null)
            dispatch(getSessionDetail(sessionId))
        }
    }

    const handleEditIncident = (inc) => {
        setEditingIncidentId(inc._id)
        setIncidentType(inc.type || 'minor')
        setIncidentDesc(inc.description || '')
        const match = sessionStudents.find(s => String(s.id) === String(inc.student) || s.name === inc.studentName)
        setIncidentStudent(match ? String(match.id) : '')
    }

    const handleDeleteIncident = async (incidentId) => {
        if (!session) return
        const result = await Swal.fire({
            title: t('teacher.classes.deleteIncidentConfirm'),
            text: t('teacher.classes.deleteIncidentText'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('global.yes'),
            cancelButtonText: t('global.cancel')
        })
        if (result.isConfirmed) {
            const ok = await dispatch(deleteIncident(sessionKey(), incidentId))
            if (ok) dispatch(getSessionDetail(sessionId))
        }
    }

    const handleFileSelect = async (e) => {
        const files = e.target.files
        if (!files || files.length === 0 || !session) return
        setUploadingMaterial(true)
        try {
            const formData = new FormData()
            Array.from(files).forEach(f => formData.append('files', f))
            const { data, status } = await Axios.post('/api/upload/uploadfiles', formData, {
                headers: { auth: localStorage.getItem('jwt'), 'Content-Type': 'multipart/form-data' }
            })
            if (status === 200 && Array.isArray(data)) {
                for (const f of data) {
                    await dispatch(addMaterial(sessionKey(), { name: f.name, url: f.url, type: 'document' }))
                }
                dispatch(getSessionDetail(sessionId))
            }
        } catch (error) {
            toastError(t('teacher.communications.uploadError'))
        } finally {
            setUploadingMaterial(false)
            e.target.value = ''
        }
    }

    const handleDeleteMaterial = async (materialId) => {
        if (!session) return
        const ok = await dispatch(removeMaterial(sessionKey(), materialId))
        if (ok) dispatch(getSessionDetail(sessionId))
    }

    const attendanceSummary = () => {
        const counts = { present: 0, absent: 0, late: 0, justified: 0, pending: 0 }
        sessionStudents.forEach(s => {
            const st = attendance[s.id] || 'present'
            counts[st] = (counts[st] || 0) + 1
        })
        return counts
    }

    const teacherOptions = availableTeachers.map(teacher => ({
        value: teacher.id,
        label: `${teacher.name} (${teacher.email})`,
        teacher
    }))

    if (loading && !sessionDetail) return <Loader />

    return (
        <Container className='my-3 p-5'>
            <Row>
                <Col className='d-flex justify-content-between align-items-center'>
                    <div>
                        <Link to='/teacher/classes' className='text-decoration-none mb-1 d-inline-block'>{t('global.return')}</Link>
                        <h3 className='fw-bold mb-1'>{session?.groupName || t('teacher.classes.session')} — {session?.name}</h3>
                        <p className='text-muted mb-0'>
                            {session?.startTime} - {session?.endTime}
                            {session?.classroom && <> · {session.classroom}</>}
                            {session?.center && <> · {session.center}</>}
                        </p>
                    </div>
                    <div className='d-flex gap-2 align-items-center'>
                        {saveState === 'saving' && (
                            <span className='text-muted' style={{ fontSize: '12px' }}>
                                <span className='spinner-border spinner-border-sm me-1'></span>{t('teacher.classes.autosaving')}
                            </span>
                        )}
                        {saveState === 'saved' && (
                            <span className='text-success' style={{ fontSize: '12px' }}>
                                <i className='fa-solid fa-check me-1'></i>{t('teacher.classes.autosaved')}
                            </span>
                        )}
                        {isClosed && (
                            <Badge bg='success' className='rounded-pill px-3 py-2'>
                                {t('teacher.classes.completed')}
                            </Badge>
                        )}
                        {hasSubstitute && (
                            <Badge bg='info' className='rounded-pill px-3 py-2'>
                                {t('teacher.classes.substitute')}: {localSession.substituteTeacherName}
                            </Badge>
                        )}
                        <Badge bg={session?.isOnline ? 'danger' : 'primary'} className='rounded-pill px-3 py-2'>
                            {session?.isOnline ? t('global.online') : t('global.inperson')}
                        </Badge>
                        {!isClosed && (
                            <Button variant='success' className='rounded-3' onClick={() => setShowCloseModal(true)}>
                                {t('teacher.classes.clossession')}
                            </Button>
                        )}
                    </div>
                </Col>
            </Row>

            {!hasSubstitute && (
                <Row className='mt-3'>
                    <Col>
                        <Button variant='outline-warning' className='rounded-3' onClick={handleOpenSubstituteModal}>
                            {t('teacher.classes.assignSubstitute')}
                        </Button>
                    </Col>
                </Row>
            )}

            {hasSubstitute && (
                <Row className='mt-3'>
                    <Col>
                        <Button variant='outline-danger' size='sm' className='rounded-3' onClick={handleRemoveSubstitute}>
                            {t('teacher.classes.removeSubstitute')}
                        </Button>
                    </Col>
                </Row>
            )}

            <Row className='mt-4'>
                <Col>
                    <Card className='border-0 shadow-sm rounded-4'>
                        <Card.Body className='p-0'>
                            <Nav variant="tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className='px-4 pt-3'>
                                <Nav.Item>
                                    <Nav.Link eventKey="attendance">{t('teacher.classes.attendance')}</Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="observations">{t('teacher.classes.observations')}</Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="content">{t('teacher.classes.content')}</Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="incidents">{t('teacher.classes.incidents')}</Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="materials">{t('teacher.classes.material')}</Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="tasks">{t('teacher.tasks.title')}</Nav.Link>
                                </Nav.Item>
                            </Nav>

                            <div className='p-4'>
                                {activeTab === 'attendance' && (
                                    <div>
                                        <div className='d-flex justify-content-between align-items-center mb-3'>
                                            <p className='fw-bold mb-0'>{t('teacher.classes.attendance')} — {sessionStudents.length} {t('teacher.classes.students')}</p>
                                        </div>
                                        {sessionStudents.length > 0
                                            ? sessionStudents.map(student => {
                                                const st = attendance[student.id] || 'present'
                                                return (
                                                <div key={student.id} className='p-3 mb-2 bg-light rounded-3'>
                                                    <div className='d-flex align-items-center justify-content-between'>
                                                        <span className='fw-semibold'>{student.name}</span>
                                                        <div className='d-flex gap-2 align-items-center'>
                                                            <Button size='sm' variant={st === 'present' ? 'success' : 'outline-success'} onClick={() => markAttendance(student.id, 'present')}>{t('teacher.classes.present')}</Button>
                                                            <Button size='sm' variant={st === 'absent' ? 'danger' : 'outline-danger'} onClick={() => markAttendance(student.id, 'absent')}>{t('teacher.classes.absent')}</Button>
                                                            <Button size='sm' variant={st === 'late' ? 'warning' : 'outline-warning'} onClick={() => markAttendance(student.id, 'late')}>{t('teacher.classes.late')}</Button>
                                                            <Button size='sm' variant={st === 'justified' ? 'info' : 'outline-info'} onClick={() => markAttendance(student.id, 'justified')}>{t('teacher.classes.justified')}</Button>
                                                            <Button size='sm' variant='link' className='text-muted p-0 ms-1' title={t('teacher.classes.addobservation')}
                                                                onClick={() => setOpenObsId(openObsId === student.id ? null : student.id)}>
                                                                <i className={`fa-regular ${studentObs[student.id] ? 'fa-comment-dots' : 'fa-comment'}`}></i>
                                                            </Button>
                                                        </div>
                                                    </div>
                                                    {openObsId === student.id && (
                                                        <Form.Control
                                                            as='textarea' rows={2}
                                                            className='mt-2 bg-white'
                                                            placeholder={t('teacher.classes.observationPlaceholder')}
                                                            style={{ borderRadius: '10px', fontSize: '13px' }}
                                                            value={studentObs[student.id] || ''}
                                                            onChange={e => { markDirty(); setStudentObs(prev => ({ ...prev, [student.id]: e.target.value })) }}
                                                        />
                                                    )}
                                                    {openObsId !== student.id && studentObs[student.id] && (
                                                        <p className='text-muted mb-0 mt-1' style={{ fontSize: '12px' }}>{studentObs[student.id]}</p>
                                                    )}
                                                </div>
                                                )
                                            })
                                            : <p className='text-muted'>{t('teacher.classes.nostudents')}</p>
                                        }
                                    </div>
                                )}
                                {activeTab === 'observations' && (
                                    <div>
                                        <div className='d-flex justify-content-between align-items-center mb-3'>
                                            <p className='fw-bold mb-0'>{t('teacher.classes.observations')}</p>
                                        </div>
                                        <Form.Group>
                                            <Form.Control as='textarea' rows={6} placeholder={t('teacher.classes.noobservations')}
                                                style={{ borderRadius: '10px' }}
                                                value={observations} onChange={e => { markDirty(); setObservations(e.target.value) }} />
                                        </Form.Group>
                                    </div>
                                )}
                                {activeTab === 'content' && (
                                    <div>
                                        <div className='d-flex justify-content-between align-items-center mb-3'>
                                            <p className='fw-bold mb-0'>{t('teacher.classes.content')}</p>
                                        </div>
                                        <Form.Group>
                                            <Form.Control as='textarea' rows={6} placeholder={t('teacher.classes.contentplaceholder')}
                                                style={{ borderRadius: '10px' }}
                                                value={content} onChange={e => { markDirty(); setContent(e.target.value) }} />
                                        </Form.Group>
                                    </div>
                                )}
                                {activeTab === 'incidents' && (
                                    <div>
                                        <p className='fw-bold mb-3'>{t('teacher.classes.incidents')}</p>
                                        <Row className='g-2 mb-3'>
                                            <Col xs={12} md={4}>
                                                <Form.Select
                                                    style={{ borderRadius: '10px' }}
                                                    value={incidentStudent}
                                                    onChange={e => setIncidentStudent(e.target.value)}
                                                >
                                                    <option value=''>{t('teacher.communications.selectStudent')}</option>
                                                    {sessionStudents.map(s => (
                                                        <option key={s.id} value={s.id}>{s.name}</option>
                                                    ))}
                                                </Form.Select>
                                            </Col>
                                            <Col xs={6} md={3}>
                                                <Form.Select
                                                    style={{ borderRadius: '10px' }}
                                                    value={incidentType}
                                                    onChange={e => setIncidentType(e.target.value)}
                                                >
                                                    <option value='minor'>{t('teacher.students.incidentminor')}</option>
                                                    <option value='major'>{t('teacher.students.incidentmajor')}</option>
                                                </Form.Select>
                                            </Col>
                                            <Col xs={6} md={3}>
                                                <Button variant='primary' className='rounded-3 w-100' onClick={handleSaveIncident} disabled={!incidentDesc.trim()}>
                                                    {editingIncidentId ? t('global.save') : t('teacher.classes.addincident')}
                                                </Button>
                                            </Col>
                                        </Row>
                                        <Form.Group className='mb-3'>
                                            <Form.Control
                                                as='textarea' rows={2}
                                                placeholder={t('teacher.students.incidentplaceholder')}
                                                style={{ borderRadius: '10px' }}
                                                value={incidentDesc}
                                                onChange={e => setIncidentDesc(e.target.value)}
                                            />
                                        </Form.Group>
                                        {localSession?.incidents?.length > 0
                                            ? localSession.incidents.map(inc => (
                                                <div key={inc._id} className='d-flex align-items-start justify-content-between p-3 mb-2 bg-light rounded-3'>
                                                    <div className='flex-grow-1'>
                                                        <div className='d-flex align-items-center gap-2 mb-1'>
                                                            <span className={`badge rounded-pill ${inc.type === 'major' ? 'bg-danger' : 'bg-warning'}`}>
                                                                {inc.type === 'major' ? t('teacher.students.incidentmajor') : t('teacher.students.incidentminor')}
                                                            </span>
                                                            <span className='fw-semibold' style={{ fontSize: '14px' }}>{inc.studentName}</span>
                                                            <span className='text-muted' style={{ fontSize: '11px' }}>
                                                                {inc.date ? new Date(inc.date).toLocaleDateString() : ''}
                                                            </span>
                                                        </div>
                                                        <p className='mb-0' style={{ fontSize: '13px' }}>{inc.description}</p>
                                                    </div>
                                                    <div className='d-flex gap-1 flex-shrink-0 ms-2'>
                                                        <Button variant='link' size='sm' className='text-muted p-1' onClick={() => handleEditIncident(inc)}>
                                                            <i className='fa-solid fa-pen'></i>
                                                        </Button>
                                                        <Button variant='link' size='sm' className='text-danger p-1' onClick={() => handleDeleteIncident(inc._id)}>
                                                            <i className='fa-solid fa-trash'></i>
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))
                                            : <p className='text-muted'>{t('teacher.classes.noincidents')}</p>
                                        }
                                    </div>
                                )}
                                {activeTab === 'materials' && (
                                    <div>
                                        <div className='d-flex justify-content-between align-items-center mb-3'>
                                            <p className='fw-bold mb-0'>{t('teacher.classes.material')}</p>
                                            <div className='d-flex align-items-center gap-2'>
                                                {uploadingMaterial && <span className='spinner-border spinner-border-sm text-secondary'></span>}
                                                <Form.Control
                                                    type='file' multiple
                                                    onChange={handleFileSelect}
                                                    disabled={uploadingMaterial}
                                                    style={{ borderRadius: '10px', maxWidth: '280px' }}
                                                />
                                            </div>
                                        </div>
                                        {localSession?.materialsAttached?.length > 0
                                            ? localSession.materialsAttached.map(m => (
                                                <div key={m._id} className='d-flex align-items-center justify-content-between p-3 mb-2 bg-light rounded-3'>
                                                    <span style={{ fontSize: '14px' }}>
                                                        <i className='fa-regular fa-file me-2 text-muted'></i>
                                                        {m.url ? <a href={m.url} target='_blank' rel='noreferrer'>{m.name || m.url}</a> : <span>{m.name}</span>}
                                                    </span>
                                                    <Button variant='link' size='sm' className='text-danger p-1 flex-shrink-0' onClick={() => handleDeleteMaterial(m._id)}>
                                                        <i className='fa-solid fa-trash'></i>
                                                    </Button>
                                                </div>
                                            ))
                                            : <p className='text-muted'>{t('teacher.classes.nomaterials')}</p>
                                        }
                                    </div>
                                )}
                                {activeTab === 'tasks' && (
                                    <div>
                                        <p className='fw-bold mb-3'>{t('teacher.tasks.title')}</p>
                                        {localSession?.tasksAssigned?.length > 0
                                            ? localSession.tasksAssigned.map((task, idx) => (
                                                <div key={idx} className='d-flex align-items-start p-3 mb-2 bg-light rounded-3'>
                                                    <div className='flex-grow-1'>
                                                        <p className='fw-semibold mb-1'>{task.title}</p>
                                                        <p className='text-muted mb-0' style={{ fontSize: '13px' }}>
                                                            {task.description || t('teacher.classes.nosessiondescription')}
                                                            {task.dueDate ? ` | ${t('teacher.classes.due')} ${new Date(task.dueDate).toLocaleDateString()}` : ''}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))
                                            : <p className='text-muted'>{t('teacher.classes.notasksinsession')}</p>
                                        }
                                    </div>
                                )}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Modal show={showSubstituteModal} onHide={() => setShowSubstituteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t('teacher.classes.assignSubstitute')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className='text-muted mb-3'>{t('teacher.classes.selectSubstituteTeacher')}</p>
                    <Select
                        options={teacherOptions}
                        value={selectedTeacher}
                        onChange={(option) => setSelectedTeacher(option)}
                        placeholder={t('teacher.classes.selectTeacherPlaceholder')}
                        isClearable
                        className='mb-2'
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant='secondary' className='rounded-3' onClick={() => setShowSubstituteModal(false)}>
                        {t('global.cancel')}
                    </Button>
                    <Button variant='primary' className='rounded-3' onClick={handleAssignSubstitute}>
                        {t('global.register')}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showCloseModal} onHide={() => setShowCloseModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t('teacher.classes.clossession')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className='text-muted mb-3' style={{ fontSize: '14px' }}>{t('teacher.classes.closereviewtext')}</p>
                    <div className='p-3 mb-2 bg-light rounded-3'>
                        <p className='fw-semibold mb-2' style={{ fontSize: '14px' }}>{t('teacher.classes.attendance')} — {sessionStudents.length} {t('teacher.classes.students')}</p>
                        <div className='d-flex flex-wrap gap-2'>
                            <Badge bg='success' className='rounded-pill px-3 py-1'>{t('teacher.classes.present')}: {attendanceSummary().present}</Badge>
                            <Badge bg='danger' className='rounded-pill px-3 py-1'>{t('teacher.classes.absent')}: {attendanceSummary().absent}</Badge>
                            <Badge bg='warning' className='rounded-pill px-3 py-1'>{t('teacher.classes.late')}: {attendanceSummary().late}</Badge>
                            <Badge bg='info' className='rounded-pill px-3 py-1'>{t('teacher.classes.justified')}: {attendanceSummary().justified}</Badge>
                            {attendanceSummary().pending > 0 && (
                                <Badge bg='secondary' className='rounded-pill px-3 py-1'>{t('teacher.classes.pending')}: {attendanceSummary().pending}</Badge>
                            )}
                        </div>
                    </div>
                    <div className='p-3 mb-2 bg-light rounded-3'>
                        <p className='fw-semibold mb-1' style={{ fontSize: '14px' }}>{t('teacher.classes.content')}</p>
                        <p className='text-muted mb-0' style={{ fontSize: '13px' }}>{content || observations || t('teacher.classes.nosessiondescription')}</p>
                    </div>
                    <div className='p-3 bg-light rounded-3'>
                        <p className='fw-semibold mb-1' style={{ fontSize: '14px' }}>{t('teacher.tasks.title')}</p>
                        <p className='text-muted mb-0' style={{ fontSize: '13px' }}>
                            {localSession?.tasksAssigned?.length || 0} {t('teacher.tasks.title').toLowerCase()}
                        </p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant='secondary' className='rounded-3' onClick={() => setShowCloseModal(false)}>
                        {t('global.cancel')}
                    </Button>
                    <Button variant='success' className='rounded-3' onClick={handleCloseSession}>
                        {t('teacher.classes.clossession')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    )
}
